/**
 * FauxDS CheckboxGroup - Recreated from Figma MCP (based on RadioGroup specs)
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate CheckboxGroup component for prototyping.
 * It is NOT the real @faire/slate CheckboxGroup and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate CheckboxGroup, see: @faire/slate/components/checkbox
 *
 * This component was created using the same specs as RadioGroup but with checkboxes.
 * - Based on Figma node: 47696:10469 (Slate Radio Group specs)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Disabled (true/false), Error (true/false)
 * - Header label support verified
 * - Error text support verified
 * - Proper spacing maintained for multiple checkboxes in a line
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

import Checkbox from "./Checkbox";
import type { CheckboxProps } from "./Checkbox";

export interface CheckboxGroupProps {
  /**
   * Header label text
   * @default "Header label"
   */
  label?: string;
  /**
   * Whether the entire group is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether to show error state (boolean or error message string)
   * @default false
   */
  error?: boolean | string;
  /**
   * Checkbox children. Each <Checkbox> manages its own state (checked/defaultChecked/onChange).
   */
  children?: React.ReactNode;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * CheckboxGroup - FauxDS (recreated from Figma MCP specs, using checkboxes)
 *
 * CheckboxGroup allows users to select one or more options among a set of options.
 * Options are typically supported by text labels.
 *
 * Usage tips:
 * - Provide a clear label for each option
 * - Pre-select default options when possible
 * - Multiple options can be selected simultaneously
 * - If possible, order options in a way that makes logical sense
 *
 * This implementation was created using the same specs as RadioGroup.
 * Spacing is maintained when multiple checkboxes are used in a line.
 *
 * Usage:
 * <CheckboxGroup label="Features">
 *   <Checkbox value="opt1" label="Option 1" />
 *   <Checkbox value="opt2" label="Option 2" />
 * </CheckboxGroup>
 */
const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  ({ label, disabled = false, error = false, children, className }, ref) => {
    const groupId = React.useId();
    const groupName = `checkbox-group-${groupId}`;

    const headerLabelColor = disabled
      ? "text-(--slate-color-action-text-disabled)"
      : error
        ? "text-(--slate-color-action-text-critical)"
        : "text-(--slate-color-action-text-default)";

    const errorTextColor = "text-(--slate-color-action-text-critical)";

    const checkboxChildren = React.Children.map(children, (child, index) => {
      if (
        !React.isValidElement<CheckboxProps>(child) ||
        child.type !== Checkbox
      ) {
        return <React.Fragment key={`invalid-child-${index}`} />;
      }
      const checkboxChild = child as React.ReactElement<CheckboxProps>;
      return React.cloneElement(checkboxChild, {
        key: checkboxChild.key ?? index,
        name: groupName,
        disabled: disabled || checkboxChild.props.disabled,
      });
    });

    return (
      <div ref={ref} className={cn("flex flex-col", className)} role="group">
        {/* Header Label */}
        {label && (
          <label
            className={cn("mb-(--slate-dimensions-2)", headerLabelColor)}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-200)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-200)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {label}
          </label>
        )}

        {/* Checkbox Options */}
        <div className="flex flex-col gap-(--slate-dimensions-2)">
          {checkboxChildren}
        </div>

        {/* Error Text */}
        {error && typeof error === "string" && (
          <p
            className={cn("mt-(--slate-dimensions-2)", errorTextColor)}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-100)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-100)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

CheckboxGroup.displayName = "CheckboxGroup";

export default CheckboxGroup;
